import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { IndianRupee, CalendarDays, CalendarRange, Clock, AlertTriangle, Hourglass } from 'lucide-react';
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, Legend, BarChart, Bar,
} from 'recharts';
import { getWalletRechargeStats } from '../api/walletRecharges';
import StatCard from '../components/ui/StatCard';
import Card from '../components/ui/Card';
import DatePicker from '../components/ui/DatePicker';
import { StatCardSkeleton, Skeleton } from '../components/ui/Skeleton';
import { formatCurrency } from '../lib/utils';

// Fixed, reused everywhere status is shown in this admin panel (Badge variants
// on the Wallet Top-ups page) — keeping the same status -> color mapping
// here so the charts read consistently with the rest of the app.
const STATUS_COLOR = {
  completed: '#22c55e',
  pending: '#eab308',
  created: '#eab308',
  failed: '#ef4444',
  refunded: '#3b82f6',
};
const STATUS_LABEL = {
  completed: 'Completed',
  pending: 'Pending',
  created: 'Created',
  failed: 'Failed',
  refunded: 'Refunded',
};
const METHOD_COLOR = ['#f59e0b', '#4f46e5', '#10b981'];

const DAY_OPTIONS = [7, 30, 90];
const TODAY_ISO = new Date().toISOString().slice(0, 10);

function formatDayLabel(isoDate) {
  return new Date(`${isoDate}T00:00:00`).toLocaleDateString('en-IN', {
    weekday: 'short', month: 'short', day: 'numeric', year: 'numeric',
  });
}

export default function Collections() {
  const [days, setDays] = useState(30);
  const [selectedDate, setSelectedDate] = useState('');

  const { data: stats, isLoading } = useQuery({
    queryKey: ['wallet-recharge-stats', days],
    queryFn: () => getWalletRechargeStats(days).then((r) => r.data),
  });

  function handlePickDay(value) {
    setSelectedDate(value);
    if (!value) return;
    const diffDays = Math.floor((Date.now() - new Date(`${value}T00:00:00`).getTime()) / 86400000);
    // Widen the fetch window so the daily series actually covers the picked date.
    if (diffDays > days) setDays(diffDays > 30 ? 90 : 30);
  }

  const selectedDayEntry = selectedDate ? (stats?.daily ?? []).find((d) => d.date === selectedDate) : null;
  const selectedDateTooFarBack = Boolean(
    selectedDate && !isLoading && !selectedDayEntry &&
    Math.floor((Date.now() - new Date(`${selectedDate}T00:00:00`).getTime()) / 86400000) > 90
  );

  const dailyData = useMemo(
    () =>
      (stats?.daily ?? []).map((d) => ({
        date: new Date(d.date).toLocaleDateString('en-IN', { month: 'short', day: 'numeric' }),
        amount: d.amount,
      })),
    [stats]
  );

  const statusData = useMemo(
    () =>
      (stats?.by_status ?? [])
        .filter((s) => s.count > 0)
        .map((s) => ({ name: STATUS_LABEL[s.status] ?? s.status, value: s.count, amount: s.amount, status: s.status })),
    [stats]
  );

  const methodData = useMemo(
    () =>
      (stats?.by_method ?? []).map((m) => ({ name: m.method.toUpperCase(), amount: m.amount })),
    [stats]
  );

  const statCards = [
    { label: 'Total Collected', value: formatCurrency(stats?.total_collected), icon: IndianRupee, color: 'indigo' },
    { label: 'This Month', value: formatCurrency(stats?.month_collected), icon: CalendarRange, color: 'green' },
    { label: 'This Week', value: formatCurrency(stats?.week_collected), icon: CalendarDays, color: 'blue' },
    { label: 'Today', value: formatCurrency(stats?.today_collected), icon: Clock, color: 'purple' },
    { label: 'Awaiting Payment', value: stats?.pending_count, icon: Hourglass, color: 'yellow' },
    { label: 'Failed', value: stats?.failed_count, icon: AlertTriangle, color: 'red' },
  ];

  return (
    <div className="space-y-8">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
        <div>
          <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100">Collections</h2>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">Driver wallet top-ups collected via Razorpay</p>
        </div>

        <div className="flex flex-col sm:flex-row sm:items-center gap-3">
          <div className="flex gap-1 bg-gray-100 dark:bg-gray-800 p-1 rounded-xl shrink-0">
            {DAY_OPTIONS.map((d) => (
              <button
                key={d}
                onClick={() => { setDays(d); setSelectedDate(''); }}
                className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                  days === d && !selectedDate
                    ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 shadow-sm'
                    : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'
                }`}
              >
                {d}d
              </button>
            ))}
          </div>

          <div className="w-40 shrink-0">
            <DatePicker
              size="sm"
              value={selectedDate}
              onChange={handlePickDay}
              max={TODAY_ISO}
              placeholder="Pick a day"
            />
          </div>
        </div>
      </div>

      {selectedDate && (
        <Card className="flex items-center justify-between flex-wrap gap-4 border-indigo-200 dark:border-indigo-900 bg-indigo-50/60 dark:bg-indigo-950/30">
          <div className="flex items-center gap-3 min-w-0">
            <div className="rounded-xl p-2.5 bg-indigo-100 dark:bg-indigo-900/40 text-indigo-600 dark:text-indigo-400 shrink-0">
              <CalendarDays size={18} />
            </div>
            <div className="min-w-0">
              <p className="text-xs text-gray-500 dark:text-gray-400">Collected on</p>
              <p className="text-sm font-semibold text-gray-900 dark:text-gray-100 truncate">{formatDayLabel(selectedDate)}</p>
            </div>
          </div>
          <div className="text-right">
            {isLoading ? (
              <Skeleton className="h-7 w-28" />
            ) : selectedDateTooFarBack ? (
              <p className="text-sm text-gray-400">Only the last 90 days are available</p>
            ) : (
              <p className="text-xl font-bold text-indigo-600 dark:text-indigo-400 whitespace-nowrap">
                {formatCurrency(selectedDayEntry?.amount ?? 0)}
              </p>
            )}
          </div>
        </Card>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
        {isLoading
          ? Array.from({ length: 6 }).map((_, i) => <StatCardSkeleton key={i} />)
          : statCards.map((card) => <StatCard key={card.label} {...card} />)}
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
        <Card className="xl:col-span-2" padding={false}>
          <div className="px-6 pt-5 pb-2 border-b border-gray-100 dark:border-gray-700">
            <h3 className="font-semibold text-gray-900 dark:text-gray-100">Collections Over Time</h3>
            <p className="text-xs text-gray-400 mt-0.5">Last {days} days · completed top-ups only</p>
          </div>
          <div className="p-4">
            {isLoading ? (
              <div className="h-[260px] flex items-center justify-center">
                <Skeleton className="h-full w-full rounded-xl" />
              </div>
            ) : (
              <ResponsiveContainer width="100%" height={260}>
                <AreaChart data={dailyData} margin={{ top: 5, right: 20, left: -10, bottom: 5 }}>
                  <defs>
                    <linearGradient id="collectedFill" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#f59e0b" stopOpacity={0.35} />
                      <stop offset="100%" stopColor="#f59e0b" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(107,114,128,0.15)" />
                  <XAxis
                    dataKey="date"
                    tick={{ fontSize: 11, fill: '#9ca3af' }}
                    tickLine={false}
                    axisLine={false}
                    interval={Math.max(0, Math.floor(days / 8))}
                  />
                  <YAxis tick={{ fontSize: 11, fill: '#9ca3af' }} tickLine={false} axisLine={false} />
                  <Tooltip
                    formatter={(v) => formatCurrency(v)}
                    contentStyle={{ background: 'var(--tw-bg, #fff)', border: '1px solid #e5e7eb', borderRadius: '12px', fontSize: 12 }}
                  />
                  <Area
                    type="monotone"
                    dataKey="amount"
                    stroke="#f59e0b"
                    strokeWidth={2.5}
                    fill="url(#collectedFill)"
                    dot={false}
                    activeDot={{ r: 5 }}
                  />
                </AreaChart>
              </ResponsiveContainer>
            )}
          </div>
        </Card>

        <Card padding={false}>
          <div className="px-6 pt-5 pb-2 border-b border-gray-100 dark:border-gray-700">
            <h3 className="font-semibold text-gray-900 dark:text-gray-100">Status Breakdown</h3>
            <p className="text-xs text-gray-400 mt-0.5">By count, all time</p>
          </div>
          <div className="p-4">
            {isLoading ? (
              <div className="h-64 flex items-center justify-center">
                <Skeleton className="h-40 w-40 rounded-full" />
              </div>
            ) : statusData.length > 0 ? (
              <ResponsiveContainer width="100%" height={260}>
                <PieChart>
                  <Pie
                    data={statusData}
                    cx="50%"
                    cy="45%"
                    innerRadius={65}
                    outerRadius={90}
                    dataKey="value"
                    paddingAngle={3}
                  >
                    {statusData.map((d, i) => (
                      <Cell key={i} fill={STATUS_COLOR[d.status] ?? '#9ca3af'} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(v, _n, item) => [`${v} · ${formatCurrency(item.payload.amount)}`, item.payload.name]} />
                  <Legend iconType="circle" iconSize={8} wrapperStyle={{ fontSize: 12 }} />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-64 flex items-center justify-center text-sm text-gray-400">No data</div>
            )}
          </div>
        </Card>
      </div>

      <Card padding={false}>
        <div className="px-6 pt-5 pb-2 border-b border-gray-100 dark:border-gray-700">
          <h3 className="font-semibold text-gray-900 dark:text-gray-100">Collected by Payment Method</h3>
          <p className="text-xs text-gray-400 mt-0.5">Completed top-ups, all time</p>
        </div>
        <div className="p-4">
          {isLoading ? (
            <div className="h-[220px] flex items-center justify-center">
              <Skeleton className="h-full w-full rounded-xl" />
            </div>
          ) : methodData.length > 0 ? (
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={methodData} margin={{ top: 5, right: 20, left: -10, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(107,114,128,0.15)" vertical={false} />
                <XAxis dataKey="name" tick={{ fontSize: 11, fill: '#9ca3af' }} tickLine={false} axisLine={false} />
                <YAxis tick={{ fontSize: 11, fill: '#9ca3af' }} tickLine={false} axisLine={false} />
                <Tooltip formatter={(v) => formatCurrency(v)} contentStyle={{ background: 'var(--tw-bg, #fff)', border: '1px solid #e5e7eb', borderRadius: '12px', fontSize: 12 }} />
                <Bar dataKey="amount" radius={[6, 6, 0, 0]}>
                  {methodData.map((_, i) => (
                    <Cell key={i} fill={METHOD_COLOR[i % METHOD_COLOR.length]} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-[220px] flex items-center justify-center text-sm text-gray-400">No data</div>
          )}
        </div>
      </Card>
    </div>
  );
}
